import React, { useState } from 'react';
import classnames from 'classnames';
import { UncontrolledTooltip } from 'reactstrap';
import { generateBeatTypeString } from '@/entities/beat';
import { buildBeatButtons, buildFunctionButtons } from './handler';

type BeatMarkerButtonProps = {
  id: string;
  Icon: React.ComponentType<{ 'aria-label'?: string; className?: string }>;
  isDelete?: boolean;
  alt: string;
  onClick: () => void;
  isActive?: boolean;
  disable?: boolean;
};

const BeatMarkerButton = ({
  id,
  Icon,
  isDelete,
  alt,
  onClick,
  isActive,
  disable,
}: BeatMarkerButtonProps) => {
  const [isHover, setIsHover] = useState(false);
  return (
    <button
      id={id}
      disabled={disable && !isActive}
      onMouseOver={() => !disable && setIsHover(true)}
      onMouseOut={() => !disable && setIsHover(false)}
      onClick={onClick}
      onFocus={() => {}}
      onBlur={() => {}}
    >
      <Icon
        aria-label={alt}
        className={classnames('beat-marker-icon', {
          active: isActive || isHover,
          'beat-marker-icon--delete': isDelete,
        })}
      />
    </button>
  );
};

type BeatMarkerButtonsProps = {
  onCloseARBeatMode: () => void;
  beatTypeNumber?: number | null;
  handleChangeBeat: (beatTypeNumber: number) => void;
  isSelectingBeat: boolean;
  tooltipContainer?: any;
};

/**
 * Renders the beat-marker toolbar used to add or change N, S, V and Q beats or delete a beat while annotating ECG. Derives the active button from beatTypeNumber via generateBeatTypeString, disables every beat button while a beat is being selected, wires each click to handleChangeBeat with the matching HES beat status, and adds a close button calling onCloseARBeatMode.
 *
 * @param props - Toolbar props carrying onCloseARBeatMode, beatTypeNumber, handleChangeBeat, isSelectingBeat and tooltipContainer.
 * @returns The rendered beat-marker toolbar with its beat buttons and function buttons.
 */
const BeatMarkerButtons = ({
  onCloseARBeatMode,
  beatTypeNumber = null,
  handleChangeBeat,
  isSelectingBeat,
  tooltipContainer,
}: BeatMarkerButtonsProps) => {
  const beatTypeString = generateBeatTypeString(beatTypeNumber);
  const listBeatBtn = buildBeatButtons(beatTypeString, isSelectingBeat, handleChangeBeat);
  const listFunctionBtn = buildFunctionButtons(onCloseARBeatMode);
  /**
   * Renders one toolbar row of beat-marker buttons from a descriptor list. Maps each item to either a plain vertical divider or a BeatMarkerButton paired with an UncontrolledTooltip anchored to the item id inside the supplied tooltip container.
   *
   * @param list - Button descriptors, each either a divider marker or a beat button with id, Icon, alt, active, disable, onClick and tooltipText.
   * @returns An array of rendered divider and button-with-tooltip nodes.
   */
  const renderButton = (list: any[] = []) =>
    list.map((item) =>
      item.type === 'divider' ? (
        <div className="vertical-divide" />
      ) : (
        <>
          <BeatMarkerButton
            id={item.id}
            Icon={item.Icon}
            isDelete={item.isDelete}
            alt={item.alt}
            onClick={item.onClick}
            isActive={item.active}
            disable={item.disable}
          />
          <UncontrolledTooltip
            className="custom-tooltip"
            target={item.id}
            container={tooltipContainer}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            {item.tooltipText}
          </UncontrolledTooltip>
        </>
      ),
    );

  return (
    <div id="beatMarkerButtons">
      <div className="beat-button">{renderButton(listBeatBtn)}</div>
      <div className="function-button">{renderButton(listFunctionBtn)}</div>
    </div>
  );
};

export default BeatMarkerButtons;
